import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";
import { fetchUserDetails } from "../../services/user";
import { IApiResponse } from "../../utils/types/api-response";
import { IUser } from "../../utils/types/user";
import useToast from "../useToast";
import useAuthContext from "../useAuthContext";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import { UserPractice } from "../../modules/user-management/types/user-management.types";
import { useAppDispatch } from "../useRedux";
import {
    getPracticeSession,
    setPracticeSession,
} from "../../utils/practiceSessionStore";
import { PRACTICE_ID_SEARCH_PARAM } from "../../utils/constants";

let hasHandledUserDetailsFailure = false;

export function useFetchUserDetails() {
    const { toast } = useToast();
    const { user, logOutUser } = useAuthContext();
    const [searchParams] = useSearchParams();

    const practiceIdFromUrl = searchParams.get(PRACTICE_ID_SEARCH_PARAM);

    const dispatch = useAppDispatch();

    if (!user) {
        hasHandledUserDetailsFailure = false;
    }

    return useQuery<IUser, AxiosError<IApiResponse<IUser>>>({
        queryKey: ["user-details"],
        queryFn: fetchUserDetails,
        onSuccess: (res) => {
            hasHandledUserDetailsFailure = false;

            if (res.practices && res.practices.length > 0) {
                const currentPracticeFromSession: UserPractice | null =
                    getPracticeSession().practice;

                const practiceFromSession =
                    currentPracticeFromSession?.user_id === res.user_id
                        ? res.practices.find(
                              (p) =>
                                  p.practice_id ===
                                  currentPracticeFromSession.practice_id
                          )
                        : undefined;

                const practiceFromUrl = practiceIdFromUrl
                    ? res.practices.find(
                          (p) => p.practice_id === practiceIdFromUrl
                      )
                    : undefined;

                let basePractice: UserPractice;

                if (practiceFromUrl) {
                    basePractice = {
                        ...practiceFromUrl,
                        user_id: res.user_id,
                    };
                } else if (practiceFromSession) {
                    basePractice = {
                        ...practiceFromSession,
                        user_id: res.user_id,
                    };
                } else {
                    const defaultPractice = res.default_practice_id
                        ? res.practices.find(
                              (p) => p.practice_id === res.default_practice_id
                          )
                        : undefined;

                    basePractice = {
                        ...(defaultPractice ?? res.practices[0]),
                        user_id: res.user_id,
                    };
                }

                const nextPractice: UserPractice = basePractice;

                setPracticeSession(nextPractice);

                dispatch(
                    handleChangePractice({
                        practice: nextPractice,
                        updateLocation: false,
                    })
                );
            }
        },
        onError: (error) => {
            if (hasHandledUserDetailsFailure) {
                return;
            }

            hasHandledUserDetailsFailure = true;
            toast({
                mode: "error",
                message:
                    error?.response?.data.message ||
                    error?.message ||
                    "Could not log user in at this time",
            });
            logOutUser().catch(() => undefined);
        },
        enabled: Boolean(user),
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
}
